import React, { useState, useEffect, useCallback, useRef } from "react";
import { slides as slidesEn, siteConfig as siteConfigEn } from "../data/mock";
import { slides as slidesHr, siteConfig as siteConfigHr } from "../data/mock-hr";
import { SlideContent } from "./SlideContent";
import { NavigationDots } from "./NavigationDots";
import "../styles/presentation.css";

const Presentation = () => {
  const [lang, setLang] = useState("en");
  const slides = lang === "hr" ? slidesHr : slidesEn;
  const siteConfig = lang === "hr" ? siteConfigHr : siteConfigEn;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [direction, setDirection] = useState("down");
  const [isVisible, setIsVisible] = useState(true);
  const touchStartY = useRef(0);
  const totalSlides = slides.length;

  const goToSlide = useCallback(
    (index, dir = "down") => {
      if (isTransitioning || index < 0 || index >= totalSlides) return;
      setIsTransitioning(true);
      setIsVisible(false);
      setDirection(dir);

      setTimeout(() => {
        setCurrentSlide(index);
        setIsVisible(true);
        setTimeout(() => {
          setIsTransitioning(false);
        }, 600);
      }, 400);
    },
    [isTransitioning, totalSlides]
  );

  const nextSlide = useCallback(() => {
    if (currentSlide < totalSlides - 1) {
      goToSlide(currentSlide + 1, "down");
    }
  }, [currentSlide, totalSlides, goToSlide]);

  const prevSlide = useCallback(() => {
    if (currentSlide > 0) {
      goToSlide(currentSlide - 1, "up");
    }
  }, [currentSlide, goToSlide]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowDown" || e.key === "ArrowRight" || e.key === " ") {
        e.preventDefault();
        nextSlide();
      } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
        e.preventDefault();
        prevSlide();
      }
    };

    const handleWheel = (e) => {
      if (Math.abs(e.deltaY) > 30) {
        if (e.deltaY > 0) nextSlide();
        else prevSlide();
      }
    };

    const handleTouchStart = (e) => {
      touchStartY.current = e.touches[0].clientY;
    };

    const handleTouchEnd = (e) => {
      const diff = touchStartY.current - e.changedTouches[0].clientY;
      if (Math.abs(diff) > 50) {
        if (diff > 0) nextSlide();
        else prevSlide();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("touchstart", handleTouchStart);
    window.addEventListener("touchend", handleTouchEnd);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [nextSlide, prevSlide]);

  const handleSkipTo = (slideIndex) => {
    goToSlide(slideIndex, slideIndex > currentSlide ? "down" : "up");
  };

  const progress = ((currentSlide + 1) / totalSlides) * 100;

  return (
    <div className="pres-root">
      {/* Top progress bar */}
      <div className="pres-progress-bar">
        <div
          className="pres-progress-fill"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Header */}
      <header className="pres-header">
        <div className="pres-logo" onClick={() => goToSlide(0, "up")}>
          <span className="pres-logo-icon">&#x20BF;</span>
          <span className="pres-logo-text">{siteConfig.title}</span>
        </div>
        <div className="pres-header-right">
          <button
            className="pres-lang-toggle"
            onClick={() => setLang(lang === "en" ? "hr" : "en")}
            data-testid="lang-toggle"
            aria-label="Toggle language"
          >
            {lang === "en" ? "HR" : "EN"}
          </button>
          <span className="pres-slide-counter">
            {String(currentSlide + 1).padStart(2, "0")} /{" "}
            {String(totalSlides).padStart(2, "0")}
          </span>
          <div className="pres-nav-arrows">
            <button
              className="pres-arrow-btn"
              onClick={prevSlide}
              disabled={currentSlide === 0}
              aria-label="Previous slide"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 15l-6-6-6 6" />
              </svg>
            </button>
            <button
              className="pres-arrow-btn"
              onClick={nextSlide}
              disabled={currentSlide === totalSlides - 1}
              aria-label="Next slide"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Navigation dots */}
      <NavigationDots
        total={totalSlides}
        current={currentSlide}
        onNavigate={handleSkipTo}
      />

      {/* Slide content */}
      <main
        className={`pres-slide-container ${isVisible ? "pres-visible" : "pres-hidden"} pres-dir-${direction}`}
      >
        <SlideContent
          slide={slides[currentSlide]}
          onSkipTo={handleSkipTo}
          onNext={nextSlide}
        />
      </main>

      {/* Bottom hint */}
      {currentSlide === 0 && (
        <div className="pres-scroll-hint">
          <span>{lang === "hr" ? "Skrolajte ili koristite strelice za navigaciju" : "Scroll or press arrow keys to navigate"}</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </div>
      )}

      {/* Keyboard hint */}
      <div className="pres-keyboard-hint">
        <kbd>↑</kbd> <kbd>↓</kbd> to navigate
      </div>
    </div>
  );
};

export default Presentation;
